/**
 * =============================================================================
 * MEDIA REJECT MODAL COMPONENT
 * =============================================================================
 *
 * Modal for choosing a rejection reason when rejecting media (ADR-022 §10).
 * The reason is a CONTROLLED VOCABULARY Select — never free text — so the
 * value written to `rejection_reason` and the audit trail is always a fixed
 * enum member (anti-tip-off + projection contract).
 *
 * `SUSPECTED_ILLEGAL` is special (D-LEGALHOLD): the server remaps the reject
 * to HIDDEN + a preservation legal hold. The modal explains this when the
 * reason is selected (or when opened in the locked "report illegal" mode).
 *
 * BL-IMG-ADMIN-MODERATION-001
 *
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Box, Select, Text } from '@bigcommerce/big-design';
import { AccessibleModal } from '../common/AccessibleModal';
import { MEDIA_REJECTION_REASONS } from '@find-my-part/types';
import type { MediaRejectionReason } from '@find-my-part/types';
import { tokens } from '../../tokens';

/** Admin-facing labels for the controlled rejection-reason vocabulary. */
export const MEDIA_REJECTION_REASON_LABELS: Record<MediaRejectionReason, string> = {
  NOT_RELEVANT: 'Not relevant',
  LOW_QUALITY: 'Low quality',
  SPAM: 'Spam',
  NSFW: 'NSFW / explicit',
  PERSONAL_INFO: 'Personal information',
  SUSPECTED_ILLEGAL: 'Suspected illegal content',
  OTHER: 'Other',
};

const REASON_OPTIONS = MEDIA_REJECTION_REASONS.map((r) => ({
  value: r,
  content: MEDIA_REJECTION_REASON_LABELS[r],
}));

export interface MediaRejectModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Number of attachments the reject will apply to (1 for single). */
  targetCount: number;
  /**
   * Lock the reason to SUSPECTED_ILLEGAL (the "report illegal" action on an
   * already-approved/hidden image). The Select is hidden; the D-LEGALHOLD
   * consequence copy is always shown.
   */
  lockToSuspectedIllegal?: boolean;
  /** Confirm with the chosen reason. */
  onConfirm: (reason: MediaRejectionReason) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  /** Forwarded to A-0: re-query the CURRENT opener control at restore time. */
  restoreFocusTo?: React.ComponentProps<typeof AccessibleModal>['restoreFocusTo'];
  /** Forwarded to A-0: stable fallback when the opener is detached. */
  fallbackFocusTo?: React.ComponentProps<typeof AccessibleModal>['fallbackFocusTo'];
  testId?: string;
}

export function MediaRejectModal({
  isOpen,
  targetCount,
  lockToSuspectedIllegal = false,
  onConfirm,
  onCancel,
  isSubmitting,
  restoreFocusTo,
  fallbackFocusTo,
  testId,
}: MediaRejectModalProps): React.ReactElement {
  const [reason, setReason] = useState<MediaRejectionReason | ''>('');

  // Reset when (re)opened.
  useEffect(() => {
    if (isOpen) {
      setReason(lockToSuspectedIllegal ? 'SUSPECTED_ILLEGAL' : '');
    }
  }, [isOpen, lockToSuspectedIllegal]);

  const effectiveReason: MediaRejectionReason | '' = lockToSuspectedIllegal
    ? 'SUSPECTED_ILLEGAL'
    : reason;
  const isValid = effectiveReason !== '';
  const showsHoldNotice = effectiveReason === 'SUSPECTED_ILLEGAL';
  const plural = targetCount > 1 ? `${targetCount} images` : 'this image';

  const handleSubmit = async (): Promise<void> => {
    if (!isValid || isSubmitting) return;
    await onConfirm(effectiveReason as MediaRejectionReason);
  };

  return (
    // B-6 retrofit (M11/M19): keep-mounted A-0 dialog. ALL dismissal routes ride
    // one predicate while a submit is in flight — onClose covers the header X
    // (which big-design invokes unconditionally), closeOnEscKey covers Esc, and
    // closeOnClickOutside covers the backdrop (the landed A-Q1 pattern;
    // final-decisions Q3 / design-v3 §D.4).
    <AccessibleModal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onCancel}
      closeOnEscKey={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      restoreFocusTo={restoreFocusTo}
      fallbackFocusTo={fallbackFocusTo}
      header={lockToSuspectedIllegal ? 'Report illegal content' : 'Reject image'}
      actions={[
        {
          text: 'Cancel',
          variant: 'secondary' as const,
          onClick: onCancel,
          disabled: isSubmitting,
        },
        {
          text: lockToSuspectedIllegal ? 'Hide & preserve' : 'Reject',
          variant: 'primary' as const,
          actionType: 'destructive' as const,
          onClick: handleSubmit,
          disabled: !isValid || isSubmitting,
          isLoading: isSubmitting,
        },
      ]}
      data-testid={testId}
    >
      <Box marginBottom="medium">
        <Text>
          {lockToSuspectedIllegal
            ? `Report ${plural} as suspected illegal content.`
            : `Choose a reason for rejecting ${plural}. The reason is recorded in the audit trail and is never shown to the poster.`}
        </Text>
      </Box>

      {!lockToSuspectedIllegal && (
        <Box marginBottom="medium">
          <Select
            label="Reason"
            required
            options={REASON_OPTIONS}
            value={reason}
            onOptionChange={(value) => setReason((value as MediaRejectionReason) ?? '')}
            placeholder="Select a reason"
            disabled={isSubmitting}
            data-testid={testId ? `${testId}-reason-select` : undefined}
          />
        </Box>
      )}

      {showsHoldNotice && (
        <Box
          marginBottom="small"
          padding="small"
          style={{ backgroundColor: tokens.color.status.warning.surface, borderRadius: tokens.dimension.radius.sm }}
        >
          <Text>
            Suspected illegal content is <strong>hidden from public view and
            preserved under a legal hold</strong> — it is never deleted while the
            hold is in place (preservation duty). The poster only ever sees a
            generic removal notice.
          </Text>
        </Box>
      )}
    </AccessibleModal>
  );
}
